import React, { useEffect, useState, useRef, useContext } from "react";
import { connect } from "react-redux";
import MGrid, { ACTION_ADD, MGridColumnDefinition } from "components/Shared/MGrid";
import {
  DATA_TYPE_MONEY,
  DATA_TYPE_BOOLEAN,
  DATA_TYPE_STRING,
  DATA_TYPE_BUTTON,
} from "typeConstants/dataTypes";
import { CustomerDetailsDto, SurveyTypeListDto, CustomerSurveyTypeFormDto } from "interfaces/Mueller.Business.Models";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import { userHasRight } from "selectors/rights";
import { CUSTOMER_DETAILS_VIEW, CUSTOMER_DETAILS_EDIT } from "typeConstants/rights";
import { getSurveyTypeForms, addFormToSurveyType, updateFormOnSurveyType, removeFormFromSurveyType } from "requests/customerConfiguration";
import AddEditCustomerSurveyTypeForms from "./AddEditCustomerSurveyTypeForm";
import { PnxFormMetaData } from "./CustomerSurveyTypeDetails";

// Hardcoding these for now as they don't often change
// TODO: maybe an endpoint for getting these from [MuellerHomeowner].[dbo].[CalcTools]
export const calcToolOptions = [
  { label: "RCT", value: "RCT" },
  { label: "ISO HomeValue", value: "ISO" },
  { label: "RCT ExpressLync", value: "RCT_ExpressLync" },
  { label: "RCT FIT", value: "RCT_FIT" },
  { label: "Vedder Calc Tool", value: "VEDDER" },
  { label: "RCT High Value", value: "RCT_High_Value" },
  { label: "e2value", value: "E2VALUE" },
  { label: "RSMeans", value: "RSMEANS" },
  { label: "RCT HV", value: "RCT_HV" },
  { label: "CBVS", value: "CBVS" },
  { label: "360Value", value: "360VALUE" },
  { label: "e2Value V2", value: "e2Value_V2" },
];

interface CustomerSurveyTypeFormsProps {
  verticalDisplay: boolean;
  surveyTypeCodeOverride?: string,  // Optional to override selectedSurveyType prop from redux
  setSelectedPnxFormInfo?: (info: PnxFormMetaData | null) => void;
  // Redux
  selectedSurveyType: SurveyTypeListDto;
  customerDetails: CustomerDetailsDto;
  canView: boolean,
  canEdit: boolean,
  makeErrorToast: (text: string) => void;
}

const CustomerSurveyTypeForms = (props: CustomerSurveyTypeFormsProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [forms, setForms] = useState<CustomerSurveyTypeFormDto[]>([]);
  const dialog = useContext<DialogProviderValue>(DialogContext);
  const mounted = useRef(true);

  useEffect(() => {
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    getForms();
  }, [props.customerDetails, props.selectedSurveyType, props.surveyTypeCodeOverride]);

  const surveyTypeCode = props.surveyTypeCodeOverride ? props.surveyTypeCodeOverride : props.selectedSurveyType?.code;

  const handleForms = (forms: CustomerSurveyTypeFormDto[]) => {
    setForms([]);
    setForms(forms);
    setIsLoading(false);
  };

  const getForms = () => {
    if (props.canView) {
      getSurveyTypeForms(props.customerDetails.number, surveyTypeCode, handleForms, mounted);
    }
  };

  // Grid Columns

  const getCalcToolName = (code: string) => {
    return calcToolOptions.find(c => c.value === code)?.label;
  };

  const columns: MGridColumnDefinition[] = [
    {
      key: "name",
      name: "Name",
      width: 225,
      dataSource: "name",
      resizable: false,
      dataType: DATA_TYPE_STRING,
    },
    {
      key: "displayOrder",
      name: "Order",
      width: 60,
      dataSource: "displayOrder",
      resizable: false,
      // This should be DATA_TYPE_NUMBER, but MGrid formats negative numbers as positive so display appears wrong...
      dataType: DATA_TYPE_STRING,
    },
    {
      key: "isSupplement",
      name: "Cust Supp",
      width: 100,
      dataSource: "isSupplement",
      resizable: false,
      dataType: DATA_TYPE_BOOLEAN,
    },
    {
      key: "isFieldSupplement",
      name: "FR Supp",
      width: 100,
      dataSource: "isFieldSupplement",
      resizable: false,
      dataType: DATA_TYPE_BOOLEAN,
    },
    {
      key: "customerFee",
      name: "Customer Fee",
      width: 110,
      dataSource: "customerFee",
      resizable: false,
      dataType: DATA_TYPE_MONEY,
    },
    {
      key: "isOnSite",
      name: "On Site",
      width: 100,
      dataSource: "isOnSite",
      resizable: false,
      dataType: DATA_TYPE_BOOLEAN,
    },
    {
      key: "isCallCenter",
      name: "Call Center",
      width: 100,
      dataSource: "isCallCenter",
      resizable: false,
      dataType: DATA_TYPE_BOOLEAN,
    },
    {
      key: "isConsumerWeb",
      name: "Consumer Web",
      width: 100,
      dataSource: "isConsumerWeb",
      resizable: false,
      dataType: DATA_TYPE_BOOLEAN,
    },
    {
      key: "calcToolCode",
      name: "Calc Tool",
      width: 100,
      dataSource: (row: CustomerSurveyTypeFormDto) => getCalcToolName(row.calcToolCode),
      resizable: true,
      dataType: DATA_TYPE_STRING,
    },
    {
      key: "isHiddenFromFR",
      name: "Hide FR",
      width: 85,
      dataSource: "isHiddenFromFR",
      resizable: false,
      dataType: DATA_TYPE_BOOLEAN,
    },
    {
      key: "isHiddenFromQA",
      name: "Hide QA",
      width: 85,
      dataSource: "isHiddenFromQA",
      resizable: false,
      dataType: DATA_TYPE_BOOLEAN,
    },
    {
      key: "isHiddentFromCustomer",
      name: "Hide Customer",
      width: 85,
      dataSource: "isHiddentFromCustomer",
      resizable: false,
      dataType: DATA_TYPE_BOOLEAN,
    },
    {
      key: "isHiddenFromAppraiser",
      name: "Hide Appraiser",
      width: 85,
      dataSource: "isHiddenFromAppraiser",
      resizable: false,
      dataType: DATA_TYPE_BOOLEAN,
    },
    {
      key: "isHiddenFromCER",
      name: "Hide CER",
      width: 85,
      dataSource: "isHiddenFromCER",
      resizable: false,
      dataType: DATA_TYPE_BOOLEAN,
    },
  ];
  const editButton = () => <i className="md-icon material-icons">edit</i>;
  const deleteButton = () => <i className="md-icon material-icons">delete</i>;
  const EDIT_COLUMN_KEY = "EDIT_COLUMN_KEY";
  const DELETE_COLUMN_KEY = "DELETE_COLUMN_KEY";
  const editColumns = [
    {
      key: EDIT_COLUMN_KEY,
      name: "Edit",
      width: 60,
      dataSource: editButton,
      dataType: DATA_TYPE_BUTTON,
      resizable: false,
      tooltip: "Click to edit form details",
    },
    {
      key: DELETE_COLUMN_KEY,
      name: "Delete",
      width: 60,
      dataSource: deleteButton,
      dataType: DATA_TYPE_BUTTON,
      resizable: false,
      tooltip: "Click to delete form",
    },
  ];
  if (props.canEdit) {
    columns.unshift(...editColumns);
  }

  // Grid Buttons + Dialogs

  const handleGridButtons = (selectedOriginalIndex: number, columnKey: string) => {
    if (columnKey == EDIT_COLUMN_KEY) {
      dialog.show(
        "Edit Form",
        <AddEditCustomerSurveyTypeForms form={forms[selectedOriginalIndex]} onSave={onSaveEdit} onCancel={dialog.hide} />,
        null,
        dialogProps);
    }
    if (columnKey == DELETE_COLUMN_KEY) {
      dialog.showYesNo(
        "Confirm Remove Form",
        `Do you want to remove ${forms[selectedOriginalIndex].name}? This action cannot be undone.`,
        () => onConfirmDelete(selectedOriginalIndex),
        dialog.hide,
        "Remove",
        "Cancel");
    }
  };

  const dialogProps = { className: "add-survey-type-form-dialog" };
  const onAdd = () => {
    dialog.show("Add Form", <AddEditCustomerSurveyTypeForms form={null} onSave={onSaveNew} onCancel={dialog.hide} />, null, dialogProps);
  };

  const onSaveNew = (form: CustomerSurveyTypeFormDto) => {
    dialog.hide();
    setIsLoading(true);
    addFormToSurveyType(props.customerDetails.number, surveyTypeCode, form.formCode, form, getForms, mounted);
  };

  const onSaveEdit = (form: CustomerSurveyTypeFormDto) => {
    dialog.hide();
    setIsLoading(true);
    updateFormOnSurveyType(props.customerDetails.number, surveyTypeCode, form.formCode, form, getForms, mounted);
  };

  const onConfirmDelete = (index: number) => {
    setIsLoading(true);
    removeFormFromSurveyType(props.customerDetails.number, surveyTypeCode, forms[index].formCode, getForms, mounted);
  };

  const onRowClick = (index: number) => {
    props.setSelectedPnxFormInfo?.({ formName: forms[index].name, formId: forms[index].phoenixFormId });
  };

  // Render

  return (
    <MGrid
      configurationId="CustomerSurveyTypeForms"
      headerText="Forms"
      columnDefinitions={columns}
      data={forms}
      disableFilters
      onCellButtonClick={handleGridButtons}
      onRowClick={onRowClick}
      isFetchingData={isLoading || !surveyTypeCode || !props.customerDetails}
      displayNumOfItemsFound={true}
      actions={props.canEdit ? [{ code: ACTION_ADD, onClick: onAdd }] : null}
      cardStyle
      minHeight={300}
    />
  );
};

const mapStateToProps = state => ({
  selectedSurveyType: state.customer.selectedSurveyType,
  customerDetails: state.customer.customerDetails,
  canView: userHasRight(CUSTOMER_DETAILS_VIEW)(state),
  canEdit: userHasRight(CUSTOMER_DETAILS_EDIT)(state),
});

export default connect(mapStateToProps)(CustomerSurveyTypeForms);